import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { alsLokalesDatum, api, errorText, formatFrist } from '../api';
import { useAuth } from '../auth';
import type { BenutzerKurz, Frist, Kunde, Ticket, TicketTyp } from '../types';
import { KundenAuswahl } from './KundenAuswahl';
import { Dialog, Field, Notice, TYP_LABEL, TYP_SYMBOL, kundenName } from './ui';

export function ticketKunde(t: Ticket): string {
  const name = kundenName({ vorname: t.kunde_vorname, nachname: t.kunde_nachname, firma: t.kunde_firma });
  return name || t.telefon || 'unbekannter Anrufer';
}

export function TicketKarte({ t, zeigeZustaendig }: { t: Ticket; zeigeZustaendig?: boolean }) {
  const heute = !t.ueberfaellig && t.status === 'offen' && formatFrist(t.faellig_am).startsWith('heute');
  const klasse = ['ticket', t.status === 'offen' && t.ueberfaellig ? 'ueberfaellig' : '', heute ? 'heute' : ''].join(' ');
  return (
    <Link to={`/tickets/${t.id}`} className={klasse}>
      <span className="typ" title={TYP_LABEL[t.typ]}>
        {TYP_SYMBOL[t.typ]}
      </span>
      <div>
        <div className="titel">{t.titel}</div>
        <div className="meta">
          <span className="mono">{t.nummer}</span>
          <span>{ticketKunde(t)}</span>
          {t.typ === 'rueckruf' && t.kunde_id && t.kunde_telefone?.[0] && <span>☎ {t.kunde_telefone[0]}</span>}
          {zeigeZustaendig && <span>→ {t.zustaendig}</span>}
          {t.rueckruf_versuche > 0 && <span>{t.rueckruf_versuche}× nicht erreicht</span>}
        </div>
      </div>
      <div className="frist">
        {t.status === 'offen' ? (
          <>
            {t.ueberfaellig ? 'überfällig ' : 'fällig '}
            {formatFrist(t.faellig_am)}
            {t.eskaliert_am && <div className="badge bad">eskaliert</div>}
          </>
        ) : (
          <span className={t.status === 'erledigt' ? 'badge ok' : 'badge'}>{t.status}</span>
        )}
      </div>
    </Link>
  );
}

/**
 * Neues Ticket. Zuständig und Frist sind vorbelegt (ich selbst, Frist des
 * Typs) und lassen sich ändern - leer lassen lassen sie sich nicht.
 */
export function TicketNeuDialog({
  kunde: vorKunde,
  typ: vorTyp = 'rueckruf',
  onClose,
  onAngelegt,
}: {
  kunde?: Kunde | null;
  typ?: TicketTyp;
  onClose: () => void;
  onAngelegt: (t: Ticket) => void;
}) {
  const { user } = useAuth();
  const [typ, setTyp] = useState<TicketTyp>(vorTyp);
  const [kunde, setKunde] = useState<Kunde | null>(vorKunde ?? null);
  const [ohneKunde, setOhneKunde] = useState(false);
  const [telefon, setTelefon] = useState('');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [zustaendig, setZustaendig] = useState(user?.id ?? '');
  const [faellig, setFaellig] = useState('');
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<Frist[]>('/api/einstellungen/fristen').then(setFristen);
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
  }, []);

  // Frist aus dem Typ vorschlagen, solange niemand sie selbst gesetzt hat.
  const [fristAngefasst, setFristAngefasst] = useState(false);
  useEffect(() => {
    if (fristAngefasst) return;
    const f = fristen.find((x) => x.vorgang_art === 'ticket' && x.status === typ);
    if (f) setFaellig(alsLokalesDatum(new Date(Date.now() + f.frist_stunden * 3600_000)));
  }, [typ, fristen, fristAngefasst]);

  async function anlegen() {
    setBusy(true);
    setFehler('');
    try {
      const t = await api<Ticket>('/api/tickets', {
        method: 'POST',
        body: {
          typ,
          titel,
          beschreibung: beschreibung || null,
          kundeId: ohneKunde ? null : kunde?.id ?? null,
          telefon: ohneKunde ? telefon : null,
          zustaendigId: zustaendig,
          faelligAm: fristAngefasst && faellig ? new Date(faellig).toISOString() : undefined,
        },
      });
      onAngelegt(t);
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const frist = fristen.find((x) => x.vorgang_art === 'ticket' && x.status === typ);

  return (
    <Dialog
      titel="Neues Ticket"
      breit
      onClose={onClose}
      aktionen={
        <>
          <button onClick={onClose}>Abbrechen</button>
          <button className="primary" disabled={busy || !titel.trim() || (!kunde && !(ohneKunde && telefon.trim()))} onClick={() => void anlegen()}>
            Ticket anlegen
          </button>
        </>
      }
    >
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {(Object.keys(TYP_LABEL) as TicketTyp[]).map((x) => (
          <button key={x} type="button" className={x === typ ? 'aktiv' : ''} onClick={() => setTyp(x)}>
            {TYP_SYMBOL[x]} {TYP_LABEL[x]}
          </button>
        ))}
      </div>

      <div className="field">
        <span>Kunde</span>
        {ohneKunde ? (
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input placeholder="Rückrufnummer des unbekannten Anrufers" value={telefon} onChange={(e) => setTelefon(e.target.value)} inputMode="tel" />
            <button type="button" className="klein" onClick={() => setOhneKunde(false)}>
              Kunde suchen
            </button>
          </div>
        ) : (
          <>
            <KundenAuswahl gewaehlt={kunde} onWahl={setKunde} />
            {!kunde && (
              <div>
                <button type="button" className="link small" onClick={() => setOhneKunde(true)}>
                  Unbekannter Anrufer – nur Telefonnummer
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Field label="Worum geht es?">
        <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder={typ === 'rueckruf' ? 'z. B. Fragt nach Liefertermin Waschmaschine' : ''} />
      </Field>
      <Field label="Details">
        <textarea value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} style={{ minHeight: 60 }} />
      </Field>
      <div className="grid2">
        <Field label="Zuständig">
          <select value={zustaendig} onChange={(e) => setZustaendig(e.target.value)}>
            {benutzer.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
                {b.id === user?.id ? ' (ich)' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fällig bis" hint={frist ? `Vorgabe für ${frist.bezeichnung}: ${frist.frist_stunden} Std.` : undefined}>
          <input
            type="datetime-local"
            value={faellig}
            onChange={(e) => {
              setFristAngefasst(true);
              setFaellig(e.target.value);
            }}
          />
        </Field>
      </div>
    </Dialog>
  );
}
